import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { ChangeEvent, Fragment, ReactNode, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Subscription } from 'rxjs'
import { uploadFile } from 'services/compose'
import { composerTabsSelector } from 'services/compose/selectors'
import { message } from 'services/snackbar'
import { VideoCoverDialogTab } from 'services/ui'
import {
  ALLOWED_IG_COVER_PHOTO_FORMATS,
  BRAND_INSTAGRAM,
  INPUT_ACCEPT_PHOTO_STATIC,
  MAX_PHOTO_SIZE_BYTES,
  MAX_PHOTO_SIZE_MB
} from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import styles from './VideoCoverDialog.pcss'
import VideoThumbnailGenerator from './VideoThumbnailGenerator'

const TAB_VIDEO = 0
const TAB_UPLOAD = 1
const UNSUPPORTED_VIDEO_UPLOAD_HINT = 'You can still set an Instagram cover from the "Upload custom (IG only)" tab.'

interface VideoCoverDialogProps {
  videoFile?: File
  initialTab?: VideoCoverDialogTab
  coverUrl?: string
  onCoverChange?: (url: string | undefined) => void
  onClose: () => void
  /** Hosts that already dismiss on backdrop click (PPDrawer) opt out - see `wrap` below. */
  disableClickAway?: boolean
  onGenerateThumb?: (thumb: string, offset: number | undefined) => void
}

export function VideoCoverDialog(props: VideoCoverDialogProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const networks = useSelector(composerTabsSelector)
  const withInstagram = networks.includes(BRAND_INSTAGRAM)
  const uploadOnly = !props.videoFile
  const coverUrl = props.coverUrl

  const [tab, setTab] = useState(uploadOnly || props.initialTab === 'upload' ? TAB_UPLOAD : TAB_VIDEO)
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const subscription = useRef<Subscription | null>(null)

  useEffect(() => () => {
    subscription.current?.unsubscribe()
  }, [])

  const onFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so selecting the same file twice still fires change.
    e.target.value = ''
    if (!file) {
      return
    }
    if (!ALLOWED_IG_COVER_PHOTO_FORMATS.includes(file.type)) {
      dispatch(message('Please select a JPEG or PNG image file.', 'error'))
      return
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      dispatch(message(`The file size exceeds the limit. Please select an image smaller than ${MAX_PHOTO_SIZE_MB}MB.`, 'error'))
      return
    }

    subscription.current?.unsubscribe()
    setUploading(true)
    subscription.current = dispatch(uploadFile(file)).subscribe({
      next: (response: { url?: string }) => {
        setUploading(false)
        if (!response.url) {
          dispatch(message('Could not upload the cover image. Please try again.', 'error'))
          return
        }
        props.onCoverChange?.(response.url)
      },
      error: (e) => {
        const msg = e.response?.error?.message || 'Could not upload the cover image. Please try again.'
        setUploading(false)
        dispatch(message(msg, 'error'))
      }
    })
  }

  const removeCover = () => {
    props.onCoverChange?.(undefined)
  }

  // A drawer host renders its own chrome (header, close button) around this container, and a
  // click on that chrome counts as "away" - so hosts that close on backdrop click opt out.
  const wrap = (content: ReactNode) => (props.disableClickAway
    ? <div className={styles.container}>{content}</div>
    : (
      <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={props.onClose}>
        <div className={styles.container}>{content}</div>
      </ClickAwayListener>
    )
  )

  const uploadPanel = (
    <div className={styles['upload-panel']}>
      <p className={styles.hint}>
        {uploadOnly
          ? 'This cover is used for Instagram only. Other networks will select a thumbnail automatically.'
          : 'This cover is used for Instagram only. Other networks use the thumbnail picked from the video.'}
      </p>
      {coverUrl && <img className={styles['cover-preview']} src={coverUrl} alt="Instagram cover" />}
      {uploading ? <CircularProgress size={24} color="primary" /> : (
        <div className={styles.actions}>
          {coverUrl && (
            <Button color="primary" data-testid="btn-remove-cover" onClick={removeCover}>
              Remove cover
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={() => inputRef.current?.click()}>
            Upload image
          </Button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        className={styles['file-input']}
        data-testid="cover-file-input"
        accept={INPUT_ACCEPT_PHOTO_STATIC.join(',')}
        onChange={onFileSelected}
        disabled={uploading}
      />
    </div>
  )

  if (uploadOnly) {
    return wrap(uploadPanel)
  }

  const generator = (
    <VideoThumbnailGenerator
      videoFile={props.videoFile!}
      onGenerateThumb={props.onGenerateThumb!}
      unsupportedHint={withInstagram ? UNSUPPORTED_VIDEO_UPLOAD_HINT : undefined}
    />
  )

  if (!withInstagram) {
    return wrap(generator)
  }

  return wrap(
    <Fragment>
      <Tabs
        value={tab}
        className={styles.tabs}
        data-testid="cover-dialog-tabs"
        onChange={(_, value) => setTab(value)}
      >
        <Tab className={styles.tab} label="Pick from video" data-testid="cover-dialog-tab-video" />
        <Tab className={styles.tab} label="Upload custom (IG only)" data-testid="cover-dialog-tab-upload" />
      </Tabs>
      {tab === TAB_VIDEO && <div className={styles.panel}>{generator}</div>}
      {tab === TAB_UPLOAD && uploadPanel}
    </Fragment>
  )
}

export default VideoCoverDialog
